import MessageBlock from "components/shared/MessageBlock";
import { Spinner } from "components/shared/Spinner";
import { SmallBody } from "helpers/TypographyComponents";
import { useAbility } from "hooks/useAbility";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
    AdvanceProvisionRule,
    AdvanceProvisionRulesResponse,
    AdvanceProvisionRuleType,
} from "store/advanceProvision";
import {
    fetchAdvanceProvisionRules,
    saveAdvanceProvisionRules,
} from "store/advanceProvision/advance-provision-use-cases";
import { useAppDispatch } from "store/store";

import RuleBlock from "./RuleBlock";

const RULE_TYPES: AdvanceProvisionRuleType[] = ["override", "unpublished", "published"];

/** Settings › Provision des à-valoir: priority rules and the two seniority grids (book publishers). */
const AdvanceProvisionSettings = () => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const ability = useAbility();
    const canEdit = ability.can("update", "AdvanceProvision");
    const [data, setData] = useState<AdvanceProvisionRulesResponse | null>(null);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        dispatch(fetchAdvanceProvisionRules())
            .unwrap()
            .then(setData)
            .catch(() => setHasError(true));
    }, [dispatch]);

    const handleSave = (ruleType: AdvanceProvisionRuleType) => async (rules: AdvanceProvisionRule[]) => {
        const action = await dispatch(saveAdvanceProvisionRules({ ruleType, rules }));
        if (saveAdvanceProvisionRules.fulfilled.match(action)) {
            setData((current) => current && { ...current, rules: { ...current.rules, [ruleType]: action.payload } });
        }
    };

    return (
        <div className="mx-auto max-w-[65rem] px-[3rem] pb-[40px] pt-[20px]">
            <h1>{t("AdvanceProvision.Settings.Title")}</h1>
            <SmallBody color="grey">{t("AdvanceProvision.Settings.Description")}</SmallBody>
            {hasError ? (
                <div className="mt-10">
                    <MessageBlock type="error" text={t("AdvanceProvision.Settings.LoadError")} />
                </div>
            ) : !data ? (
                <div className="mt-10 flex justify-center">
                    <Spinner />
                </div>
            ) : (
                RULE_TYPES.map((ruleType) => (
                    <RuleBlock
                        key={ruleType}
                        ruleType={ruleType}
                        rules={data.rules[ruleType]}
                        defaults={data.defaults[ruleType]}
                        canEdit={canEdit}
                        onSave={handleSave(ruleType)}
                    />
                ))
            )}
        </div>
    );
};

export default AdvanceProvisionSettings;
